
import { Component, Prop } from "vue-property-decorator";
import { mixins } from "vue-class-component";
import moment from "moment";
import ResourceAvatar from "@/components/card/ResourceAvatar.vue";
import APPROVE_CONTRACTOR_APPROVE_ACCESS from "@/apollo/jobboard/ApproveContractorApproveAccess.gql";
import UPDATE_CONTRACTOR_DELETE_REQUEST from "@/apollo/jobboard/UpdateContractorDeleteRequest.gql";
import UPDATE_CONTRACTOR_LIGHT from "@/apollo/contractors/UpdateContractorLight.gql";
import UPDATE_MANAGER_CONTRACTOR from "@/apollo/jobboard/UpdateManagerContractor.gql";
import DELETE_MANAGER_CONTRACTOR from "@/apollo/jobboard/DeleteManagerContractor.gql";

import GetRelativeRouteNameMixin from "@/mixins/route/GetRelativeRouteNameMixin.vue";
import UserIcon from "@/components/icons/user.vue";
import CalendarIcon from "@/components/icons/calendar.vue";
import DollarIcon from "@/components/icons/dollar.vue";
import ProfileIcon from "@/components/icons/profile.vue";
import LocationIconNew from "@/components/icons/LocationIconNew.vue";
import CardBodyItem from "@/components/card/CardBodyItemNew.vue";
import TableIcon from "@/components/table/table-icon.vue";
import Card from "@/components/card/ResourceCards.vue";
import Processing from "@/components/common/Processing.vue";
import UiButton from "@/components/button/ui-button.vue";
import PopUpWithActivation from "@/components/common/PopUpWithActivation.vue";
import ButtonDropdown from "@/components/common/ButtonDropdown.vue";
import CardInfoChip from "@/components/card/CardInfoChip.vue";
import ItemChip from "@/components/common/ItemChip.vue";
import CardAvatar from "@/components/card/CardAvatar.vue";
import ItemChipsWithMore from "@/components/common/ItemChipsWithMore.vue";
import RemoveManagerContractorRequest from "@/components/contractors/RemoveManagerContractorRequest.vue";

import Message, { MessageDuration } from "@/data/notificationsMessages";

import { User } from "@/generated/graphql";
import RouteNames from "@/entities/RouteNames";
import Roles from "@/entities/Roles";
import { NOTIFICATION } from "@/entities/Events";
import RouteParams from "@/entities/RouteParams";
import { NO_INFORMATION } from "@/data/placeholders";

@Component({
  components: {
    CardBodyItem,
    TableIcon,
    Card,
    Processing,
    UiButton,
    ResourceAvatar,
    PopUpWithActivation,
    ButtonDropdown,
    CardInfoChip,
    UserIcon,
    LocationIconNew,
    CalendarIcon,
    DollarIcon,
    ProfileIcon,
    ItemChip,
    CardAvatar,
    ItemChipsWithMore,
    RemoveManagerContractorRequest,
  },
})
export default class RequestCardResources extends mixins(
  GetRelativeRouteNameMixin
) {
  @Prop({ type: Object, required: true }) readonly contractorUser!: User;
  @Prop({ type: Object, required: true }) readonly managerUser!: User;
  @Prop({ type: String, required: true }) readonly currentUserRole!: Roles;
  @Prop({ type: String, required: true }) readonly requestId!: string;
  @Prop({ type: String, default: undefined }) readonly requestDate?: string;
  @Prop({ type: String, required: true }) readonly contractorId!: string;
  @Prop({ type: Boolean, required: true })
  readonly contractorApproved!: boolean;
  @Prop({ type: String, required: true }) readonly managerId!: string;

  nameProvided: boolean = false;
  $confirm: any;
  processing: boolean = false;
  routes = RouteNames;
  dialog: boolean = false;
  actionsPopUpShown: boolean = true;

  get navigateToContractorProfile(): RouteParams<{ id: string }> {
    if (this.isPrivateDataShown) {
      return {
        name: this.getRelativeRouteName(RouteNames.CONTRACTOR_DETAILS_ABOUT),
        params: { id: this.contractorId },
      };
    }
    return {
      name: this.getRelativeRouteName(RouteNames.CONTRACTOR_PUBLIC_PAGE),
      params: { id: this.contractorUser.id },
    };
  }

  get isPrivateDataShown(): boolean {
    return this.contractorApproved && this.isAdmin;
  }

  get newContractor(): boolean {
    return !this.contractorApproved;
  }

  get contractorLocation(): string {
    if (!this.contractorUser.profile) {
      return NO_INFORMATION;
    }
    const { country, state, city } = this.contractorUser.profile;
    if (!country && !state && !city) {
      return NO_INFORMATION;
    }
    return `${city}, ${state}, ${country}`;
  }

  get contractorJobTitle(): string {
    return this.contractorUser.profile?.job_title || "n/a";
  }

  get contractorRates(): string | undefined {
    const minRate = this.contractorUser.profile?.min_rate;
    const maxRate = this.contractorUser.profile?.max_rate;
    if (minRate && maxRate) {
      return `$${minRate} - $${maxRate}`;
    }
    if (minRate && !maxRate) {
      return `from $${minRate}`;
    }
    if (!minRate && maxRate) {
      return `up to $${maxRate}`;
    }
    return NO_INFORMATION;;
  }

  get contractorInitials(): string {
    return `${this.contractorUser.given_name![0]}${
      this.contractorUser.family_name![0]
    }`;
  }

  get contractorAvailable(): boolean {
    return this.contractorUser.available;
  }

  get isAdmin(): boolean {
    return this.currentUserRole === Roles.ADMIN;
  }

  get isManager(): boolean {
    return this.currentUserRole === Roles.MANAGER;
  }

  get requestRevocable(): boolean {
    return this.contractorApproved;
  }

  get showAddToCompany(): boolean {
    return this.newContractor && this.isAdmin;
  }

  get showSeparationLine(): boolean {
    return !this.newContractor || this.showAddToCompany;
  }

  get contractorAvatar(): string {
    return this.contractorUser.avatar ? this.contractorUser.avatar : "";
  }

  get contractorName(): string | undefined {
    const name = this.contractorUser.given_name?.trim();
    const surname = this.contractorUser.family_name?.trim();
    if (!name && !surname) {
      return undefined;
    }
    this.nameProvided = true;
    return `${name} ${surname}`;
  }

  get contractorLink(): Object {
    return {
      name: RouteNames.CONTRACTOR_DETAILS_ABOUT,
      params: { id: this.contractorId },
    };
  }

  get contractorSkills(): any[] {
    if (this.contractorUser.profile?.skills?.length) {
      return this.contractorUser.profile.skills.reduce(
        (acc: any[], { skill }) => {
          if (skill?.name) {
            acc.push({ name: skill.name, id: skill.id });
          }
          return acc;
        },
        []
      );
    }
    return [];
  }

  get titleColor(): string {
    return this.contractorApproved ? "text-primary-500" : "text-gray-500";
  }

  get emailFontSize(): string {
    return this.nameProvided ? "text-sm" : "text-lg";
  }

  get invitationAccepted(): string {
    if (this.contractorUser.invitations[0]?.status === "DECLINED") {
      return "declined";
    }
    if (this.contractorUser.invitations[0]?.status === "ACCEPTED") {
      return "accepted";
    }
    return "not confirmed";
  }

  get profileConfirmed(): string {
    return this.contractorUser.confirmed ? "confirmed" : "not confirmed";
  }

  get requestedDate(): string | undefined {
    if (!this.requestDate) {
      return undefined;
    }
    return moment(this.requestDate).format("MMMM DD, YYYY");
  }

  async handleAddClick() {
    this.processing = true;
    await this.approveContractor(this.contractorId);
    this.$emit("refetch");
    this.processing = false;
  }

  async handleAddGiveAccessClick() {
    this.processing = true;
    await this.approveContractorApproveRequest(
      this.contractorId,
      this.requestId
    );
    this.$emit("refetch");
    this.processing = false;
  }

  async handleRejectClick() {
    this.processing = true;
    await this.deleteContractorAndRequest(this.contractorId, this.requestId);
    this.$emit("refetch");
    this.processing = false;
  }

  async handleGiveAccessClick() {
    this.processing = true;
    await this.approveAccess(this.requestId);
    this.$emit("refetch");
    this.processing = false;
  }

  async handleDenyAccessClick() {
    this.processing = true;
    await this.deleteManagerContractorRelationship(
      this.requestId,
      this.contractorUser.id,
      this.managerUser.id
    );
    this.$emit("refetch");
    this.processing = false;
  }

  deleteManagerContractorRelationship(
    requestId: string,
    contractorId: string,
    managerId: string
  ) {
    return this.$apollo.mutate({
      mutation: DELETE_MANAGER_CONTRACTOR,
      variables: {
        request: requestId,
        contractor: contractorId,
        manager: managerId,
      },
    });
  }

  approveContractor(contractorId: string) {
    return this.$apollo.mutate({
      mutation: UPDATE_CONTRACTOR_LIGHT,
      variables: {
        set: { approved: true },
        contractorId,
      },
    });
  }

  approveContractorApproveRequest(contractorId: string, requestId: string) {
    return this.$apollo.mutate({
      mutation: APPROVE_CONTRACTOR_APPROVE_ACCESS,
      variables: {
        set: { approved: true },
        manager_contractor_set: { approved: true },
        contractor: contractorId,
        request: requestId,
      },
    });
  }

  deleteContractorAndRequest(contractorId: string, requestId: string) {
    return this.$apollo.mutate({
      mutation: UPDATE_CONTRACTOR_DELETE_REQUEST,
      variables: {
        set: { deleted: true },
        contractor: contractorId,
        request: requestId,
      },
    });
  }

  approveAccess(requestId: string) {
    return this.$apollo.mutate({
      mutation: UPDATE_MANAGER_CONTRACTOR,
      variables: {
        set: { approved: true },
        request: requestId,
      },
    });
  }

  async handleApproveConfirmation(): Promise<void> {
    if (!this.newContractor) {
      await this.handleGiveAccessClick();
      return;
    }

    try {
      this.$confirm({
        title: "Approve Request",
        message: `You are going to add a new contractor to the Talent Directory and approve the manager's request`,
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: async (confirm: any) => {
          if (confirm) {
            await this.handleAddGiveAccessClick();

            this.$eventBus.emit(
              NOTIFICATION,
              Message.successMessage(
                "Contractor and manager's request were approved"
              )
            );
          }
        },
      });
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  async handleDeclineConfirmation(): Promise<void> {
    if (!this.newContractor) {
      await this.handleDenyAccessClick();
      return;
    }

    try {
      this.$confirm({
        title: "Decline Request",
        message: `You are going to decline the manager's request and the contractor will not be added to your company database`,
        button: {
          no: "No",
          yes: "Yes",
        },
        callback: async (confirm: any) => {
          if (confirm) {
            await this.handleRejectClick();

            this.$eventBus.emit(
              NOTIFICATION,
              Message.successMessage(
                "The contractor was not added to your company database and the manager's request was declined",
                MessageDuration.MEDIUM
              )
            );
          }
        },
      });
    } catch (e) {
      this.$eventBus.emit(NOTIFICATION, Message.errorMessage(e.message));
    }
  }

  async handleAddToCompanyConfirmation(): Promise<void> {
    if (this.newContractor) {
      await this.handleAddClick();
    }
  }

  showActionsPopUp(): void {
    this.actionsPopUpShown = true;
  }

  hideActionsPopUp(callback: Function): void {
    this.actionsPopUpShown = false;
    callback();
  }
}
